import { useState, useEffect } from 'react'
import { Head } from '@inertiajs/react'

// UI Components
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  ResponsiveDialog,
  ResponsiveDialogTrigger,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogClose,
} from '@/components/ui/responsive-dialog'
import { RichTextEditor } from '@/components/ui/rich-text-editor'

// Style Guide Components
import { ColorSwatch, ComponentShowcase, SectionHeader, LogoManager } from '@/components/style-guide'

// Lucide icons for demos
import { ArrowRight, Check, Loader2, Mail, Search, Settings } from 'lucide-react'

// Navigation sections
const SECTIONS = [
  { id: 'logos', label: 'Logos' },
  { id: 'colors', label: 'Colors' },
  { id: 'typography', label: 'Typography' },
  { id: 'spacing', label: 'Spacing' },
  { id: 'radius', label: 'Border Radius' },
  { id: 'buttons', label: 'Buttons' },
  { id: 'dialogs', label: 'Dialogs & Drawers' },
  { id: 'cards', label: 'Cards' },
  { id: 'forms', label: 'Form Elements' },
  { id: 'rich-text', label: 'Rich Text Editor' },
  { id: 'states', label: 'Interactive States' },
  { id: 'patterns', label: 'Patterns' },
]

// Color token definitions
const COLOR_TOKENS = [
  { name: 'background', cssVar: '--background', description: 'Page background' },
  { name: 'foreground', cssVar: '--foreground', description: 'Default text color' },
  { name: 'card', cssVar: '--card', description: 'Card backgrounds' },
  { name: 'card-foreground', cssVar: '--card-foreground', description: 'Card text' },
  { name: 'popover', cssVar: '--popover', description: 'Popover backgrounds' },
  { name: 'popover-foreground', cssVar: '--popover-foreground', description: 'Popover text' },
  { name: 'primary', cssVar: '--primary', description: 'Primary actions, buttons' },
  { name: 'primary-foreground', cssVar: '--primary-foreground', description: 'Text on primary' },
  { name: 'secondary', cssVar: '--secondary', description: 'Secondary elements' },
  { name: 'secondary-foreground', cssVar: '--secondary-foreground', description: 'Text on secondary' },
  { name: 'muted', cssVar: '--muted', description: 'Muted backgrounds' },
  { name: 'muted-foreground', cssVar: '--muted-foreground', description: 'Muted/placeholder text' },
  { name: 'accent', cssVar: '--accent', description: 'Accent highlights' },
  { name: 'accent-foreground', cssVar: '--accent-foreground', description: 'Text on accent' },
  { name: 'destructive', cssVar: '--destructive', description: 'Destructive actions' },
  { name: 'border', cssVar: '--border', description: 'Default borders' },
  { name: 'input', cssVar: '--input', description: 'Input borders' },
  { name: 'ring', cssVar: '--ring', description: 'Focus rings' },
]

const CHART_COLORS = [
  { name: 'chart-1', cssVar: '--chart-1' },
  { name: 'chart-2', cssVar: '--chart-2' },
  { name: 'chart-3', cssVar: '--chart-3' },
  { name: 'chart-4', cssVar: '--chart-4' },
  { name: 'chart-5', cssVar: '--chart-5' },
]

const SIDEBAR_COLORS = [
  { name: 'sidebar', cssVar: '--sidebar' },
  { name: 'sidebar-foreground', cssVar: '--sidebar-foreground' },
  { name: 'sidebar-primary', cssVar: '--sidebar-primary' },
  { name: 'sidebar-primary-foreground', cssVar: '--sidebar-primary-foreground' },
  { name: 'sidebar-accent', cssVar: '--sidebar-accent' },
  { name: 'sidebar-accent-foreground', cssVar: '--sidebar-accent-foreground' },
  { name: 'sidebar-border', cssVar: '--sidebar-border' },
  { name: 'sidebar-ring', cssVar: '--sidebar-ring' },
]

// Default content for rich text editor demo
const DEFAULT_EDITOR_CONTENT = `
<h1>Welcome to the Editor</h1>
<p>This is a <strong>rich text editor</strong> with <em>full formatting</em> support.</p>
<h2>Features</h2>
<ul>
  <li>Headings (H1, H2, H3)</li>
  <li>Bold, italic, and <u>underline</u></li>
  <li>Bullet and numbered lists</li>
</ul>
<blockquote>
  <p>Blockquotes look great for highlighting important information.</p>
</blockquote>
<p>You can also add <a href="https://example.com">links</a> to your content!</p>
`.trim()

export default function StyleGuide({ railsEnv, appName, logos }) {
  const [isDark, setIsDark] = useState(false)
  const [activeSection, setActiveSection] = useState('logos')
  const [editorContent, setEditorContent] = useState(DEFAULT_EDITOR_CONTENT)

  // Check initial dark mode state
  useEffect(() => {
    setIsDark(document.documentElement.classList.contains('dark'))
  }, [])

  // Dark mode toggle
  const toggleDarkMode = () => {
    const newValue = !isDark
    setIsDark(newValue)
    document.documentElement.classList.toggle('dark', newValue)
  }

  // Intersection observer for active section highlighting
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      { rootMargin: '-20% 0px -70% 0px' }
    )

    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <Head title="Style Guide" />

      <div className="min-h-screen bg-background">
        {/* Sticky Header */}
        <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
          <div className="container flex h-14 items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-bold">{appName} Style Guide</h1>
              <span className="px-2 py-1 text-xs rounded-full bg-primary text-primary-foreground">
                {railsEnv}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleDarkMode}
            >
              {isDark ? 'Light Mode' : 'Dark Mode'}
            </Button>
          </div>
        </header>

        <div className="container flex gap-8 py-8">
          {/* Sticky Sidebar Navigation */}
          <aside className="hidden lg:block w-48 shrink-0">
            <nav className="sticky top-20 space-y-1">
              {SECTIONS.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`block px-3 py-2 text-sm rounded-md transition-colors ${
                    activeSection === id
                      ? 'bg-accent text-accent-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  {label}
                </a>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0 space-y-12">

            {/* LOGOS SECTION */}
            <section>
              <SectionHeader
                id="logos"
                title="Logos"
                description="Brand logos and icons for light and dark themes"
              />
              <div className="mt-6">
                <LogoManager logos={logos} />
              </div>
            </section>

            {/* COLORS SECTION */}
            <section>
              <SectionHeader
                id="colors"
                title="Colors"
                description="Design tokens defined in application.css using OKLch color space"
              />

              {/* KF Brand Colors */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">KF Brand Colors</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Custom brand colors used on marketing pages (Home, Member, Coach)
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <div className="w-full h-16 bg-kf-red rounded-md mb-2" />
                    <span className="font-mono text-xs text-muted-foreground">kf-red</span>
                    <p className="text-xs text-muted-foreground">#c1272d</p>
                  </div>
                  <div className="text-center">
                    <div className="w-full h-16 bg-kf-black rounded-md mb-2" />
                    <span className="font-mono text-xs text-muted-foreground">kf-black</span>
                    <p className="text-xs text-muted-foreground">#0a0a0a</p>
                  </div>
                  <div className="text-center">
                    <div className="w-full h-16 bg-kf-gray rounded-md mb-2" />
                    <span className="font-mono text-xs text-muted-foreground">kf-gray</span>
                    <p className="text-xs text-muted-foreground">#1a1a1a</p>
                  </div>
                  <div className="text-center">
                    <div className="w-full h-16 bg-kf-white rounded-md border mb-2" />
                    <span className="font-mono text-xs text-muted-foreground">kf-white</span>
                    <p className="text-xs text-muted-foreground">#fafafa</p>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium mb-2">Gradient</p>
                  <div className="w-full h-16 bg-kf-gradient rounded-md mb-2" />
                  <span className="font-mono text-xs text-muted-foreground">bg-kf-gradient</span>
                  <p className="text-xs text-muted-foreground">linear-gradient(135deg, #c1272d, #a6383b)</p>
                </div>
              </div>

              {/* Core Colors */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Core Colors</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {COLOR_TOKENS.map((token) => (
                    <ColorSwatch key={token.name} {...token} />
                  ))}
                </div>
              </div>

              {/* Chart Colors */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Chart Colors</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {CHART_COLORS.map((token) => (
                    <ColorSwatch key={token.name} {...token} />
                  ))}
                </div>
              </div>

              {/* Sidebar Colors */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold mb-4">Sidebar Colors</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {SIDEBAR_COLORS.map((token) => (
                    <ColorSwatch key={token.name} {...token} />
                  ))}
                </div>
              </div>
            </section>

            {/* TYPOGRAPHY SECTION */}
            <section>
              <SectionHeader
                id="typography"
                title="Typography"
                description="Font families, sizes, and weights available via Tailwind CSS"
              />

              <div className="mt-6 space-y-8">
                {/* Font Families */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold">Font Families</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="p-4 border rounded-lg">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Display Font</p>
                      <p className="font-display text-4xl mb-2">Oswald</p>
                      <p className="font-mono text-xs text-muted-foreground">font-display</p>
                      <p className="text-sm text-muted-foreground mt-2">Used for headlines, hero text, large numbers</p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Body Font</p>
                      <p className="font-sans text-4xl mb-2">Plus Jakarta Sans</p>
                      <p className="font-mono text-xs text-muted-foreground">font-sans (default)</p>
                      <p className="text-sm text-muted-foreground mt-2">Used for body text, UI, navigation</p>
                    </div>
                  </div>

                  {/* Display Font Sizes */}
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Display Font Sizes</p>
                    {[
                      { cls: 'text-4xl', size: '36px' },
                      { cls: 'text-5xl', size: '48px' },
                      { cls: 'text-6xl', size: '60px' },
                      { cls: 'text-7xl', size: '72px' },
                    ].map(({ cls, size }) => (
                      <div key={cls} className="flex items-baseline gap-4">
                        <span className="w-24 font-mono text-xs text-muted-foreground">{cls}</span>
                        <span className={`font-display ${cls}`}>HEADLINES</span>
                        <span className="font-mono text-xs text-muted-foreground">({size})</span>
                      </div>
                    ))}
                  </div>

                  {/* Text Patterns */}
                  <div className="space-y-3">
                    <p className="text-sm font-medium">Common Text Patterns</p>
                    <div className="space-y-4">
                      <div className="flex items-baseline gap-4">
                        <span className="w-48 font-mono text-xs text-muted-foreground">uppercase tracking-wider</span>
                        <span className="uppercase tracking-wider">Labels and Navigation</span>
                      </div>
                      <div className="flex items-baseline gap-4">
                        <span className="w-48 font-mono text-xs text-muted-foreground">font-display + text-kf-red</span>
                        <span className="font-display text-2xl">HEADLINE <span className="text-kf-red">ACCENT</span></span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Font Sizes */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Font Sizes</h3>
                  {[
                    { cls: 'text-xs', label: 'text-xs (12px)' },
                    { cls: 'text-sm', label: 'text-sm (14px)' },
                    { cls: 'text-base', label: 'text-base (16px)' },
                    { cls: 'text-lg', label: 'text-lg (18px)' },
                    { cls: 'text-xl', label: 'text-xl (20px)' },
                    { cls: 'text-2xl', label: 'text-2xl (24px)' },
                    { cls: 'text-3xl', label: 'text-3xl (30px)' },
                    { cls: 'text-4xl', label: 'text-4xl (36px)' },
                  ].map(({ cls, label }) => (
                    <div key={cls} className="flex items-baseline gap-4">
                      <span className="w-32 font-mono text-xs text-muted-foreground">{cls}</span>
                      <span className={cls}>The quick brown fox jumps</span>
                    </div>
                  ))}
                </div>

                {/* Font Weights */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Font Weights</h3>
                  {[
                    { cls: 'font-light', label: 'font-light (300)' },
                    { cls: 'font-normal', label: 'font-normal (400)' },
                    { cls: 'font-medium', label: 'font-medium (500)' },
                    { cls: 'font-semibold', label: 'font-semibold (600)' },
                    { cls: 'font-bold', label: 'font-bold (700)' },
                  ].map(({ cls, label }) => (
                    <div key={cls} className="flex items-baseline gap-4">
                      <span className="w-40 font-mono text-xs text-muted-foreground">{cls}</span>
                      <span className={`text-lg ${cls}`}>The quick brown fox jumps</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* SPACING SECTION */}
            <section>
              <SectionHeader
                id="spacing"
                title="Spacing"
                description="Tailwind spacing scale (1 unit = 0.25rem = 4px)"
              />

              <div className="mt-6 space-y-2">
                {[0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24].map((n) => (
                  <div key={n} className="flex items-center gap-4">
                    <span className="w-12 font-mono text-sm text-muted-foreground text-right">{n}</span>
                    <div
                      className="h-4 bg-primary rounded"
                      style={{ width: `${n * 0.25}rem` }}
                    />
                    <span className="font-mono text-xs text-muted-foreground">
                      {n * 0.25}rem / {n * 4}px
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* BORDER RADIUS SECTION */}
            <section>
              <SectionHeader
                id="radius"
                title="Border Radius"
                description="Radius tokens defined as CSS variables"
              />

              <div className="mt-6 flex flex-wrap gap-6">
                {[
                  { name: 'rounded-sm', cssVar: '--radius-sm' },
                  { name: 'rounded-md', cssVar: '--radius-md' },
                  { name: 'rounded-lg', cssVar: '--radius-lg' },
                  { name: 'rounded-xl', cssVar: '--radius-xl' },
                  { name: 'rounded-2xl', cssVar: '--radius-2xl' },
                  { name: 'rounded-3xl', cssVar: '--radius-3xl' },
                  { name: 'rounded-4xl', cssVar: '--radius-4xl' },
                ].map(({ name, cssVar }) => (
                  <div key={name} className="text-center">
                    <div
                      className="w-20 h-20 bg-primary mb-2"
                      style={{ borderRadius: `var(${cssVar})` }}
                    />
                    <span className="font-mono text-xs text-muted-foreground">{name}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* BUTTONS SECTION */}
            <section>
              <SectionHeader
                id="buttons"
                title="Buttons"
                description="All button variants and sizes from shadcn/ui"
              />

              {/* Variants */}
              <ComponentShowcase title="Variants" description="Different visual styles">
                <Button variant="default">Default</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="destructive">Destructive</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="link">Link</Button>
              </ComponentShowcase>

              {/* Sizes */}
              <ComponentShowcase title="Sizes" description="Size variations">
                <Button size="sm">Small</Button>
                <Button size="default">Default</Button>
                <Button size="lg">Large</Button>
                <Button size="icon"><Settings className="h-4 w-4" /></Button>
                <Button size="icon-sm"><Settings className="h-4 w-4" /></Button>
                <Button size="icon-lg"><Settings className="h-4 w-4" /></Button>
              </ComponentShowcase>

              {/* With Icons */}
              <ComponentShowcase title="With Icons" description="Buttons with leading/trailing icons">
                <Button><Mail className="h-4 w-4" /> Login with Email</Button>
                <Button variant="secondary">Continue <ArrowRight className="h-4 w-4" /></Button>
                <Button variant="outline"><Search className="h-4 w-4" /> Search</Button>
              </ComponentShowcase>

              {/* States */}
              <ComponentShowcase title="States" description="Interactive and disabled states">
                <Button>Normal</Button>
                <Button disabled>Disabled</Button>
                <Button disabled><Loader2 className="h-4 w-4 animate-spin" /> Loading</Button>
              </ComponentShowcase>

              {/* KF Brand Variants */}
              <ComponentShowcase title="KF Brand Variants" description="Custom variants for marketing pages">
                <Button variant="gradient">Gradient</Button>
                <Button variant="kf-secondary">KF Secondary</Button>
                <Button variant="kf-outline">KF Outline</Button>
              </ComponentShowcase>

              {/* KF Brand with Icons */}
              <ComponentShowcase title="KF Brand with Icons" description="Marketing CTAs with arrow icons">
                <Button variant="gradient" className="group">
                  Schedule a Call
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button variant="kf-secondary" className="group">
                  Learn More
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button variant="kf-outline" className="group">
                  Contact Us
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </ComponentShowcase>

              {/* Dark Background Preview */}
              <div className="mt-6">
                <p className="text-sm font-medium mb-3">On Dark Background</p>
                <div className="bg-kf-gradient p-6 rounded-lg flex flex-wrap gap-4">
                  <Button variant="kf-secondary" className="group">
                    White on Gradient
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </section>

            {/* DIALOGS & DRAWERS SECTION */}
            <section>
              <SectionHeader
                id="dialogs"
                title="Dialogs & Drawers"
                description="Responsive modals that show as Dialog on desktop and Drawer on mobile"
              />

              <div className="mt-6 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Responsive Dialog</CardTitle>
                    <CardDescription>
                      Opens as a centered dialog on desktop (≥768px) and a bottom drawer on mobile (&lt;768px).
                      Resize your browser to see the difference.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Basic Example */}
                    <div>
                      <p className="text-sm font-medium mb-2">Basic Dialog</p>
                      <p className="text-xs text-muted-foreground mb-3">
                        Simple dialog with title and description
                      </p>
                      <ResponsiveDialog>
                        <ResponsiveDialogTrigger asChild>
                          <Button>Open Dialog</Button>
                        </ResponsiveDialogTrigger>
                        <ResponsiveDialogContent>
                          <ResponsiveDialogHeader>
                            <ResponsiveDialogTitle>Edit Profile</ResponsiveDialogTitle>
                            <ResponsiveDialogDescription>
                              Make changes to your profile here. Click save when you're done.
                            </ResponsiveDialogDescription>
                          </ResponsiveDialogHeader>
                          <div className="py-4 space-y-4">
                            <div className="space-y-2">
                              <Label htmlFor="name">Name</Label>
                              <Input id="name" defaultValue="John Doe" />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="username">Username</Label>
                              <Input id="username" defaultValue="@johndoe" />
                            </div>
                          </div>
                          <ResponsiveDialogFooter>
                            <ResponsiveDialogClose asChild>
                              <Button variant="outline">Cancel</Button>
                            </ResponsiveDialogClose>
                            <Button>Save changes</Button>
                          </ResponsiveDialogFooter>
                        </ResponsiveDialogContent>
                      </ResponsiveDialog>
                    </div>

                    {/* Delete Confirmation Example */}
                    <div>
                      <p className="text-sm font-medium mb-2">Delete Confirmation</p>
                      <p className="text-xs text-muted-foreground mb-3">
                        Destructive action confirmation dialog
                      </p>
                      <ResponsiveDialog>
                        <ResponsiveDialogTrigger asChild>
                          <Button variant="destructive">Delete Item</Button>
                        </ResponsiveDialogTrigger>
                        <ResponsiveDialogContent>
                          <ResponsiveDialogHeader>
                            <ResponsiveDialogTitle>Are you sure?</ResponsiveDialogTitle>
                            <ResponsiveDialogDescription>
                              This action cannot be undone. This will permanently delete your
                              account and remove your data from our servers.
                            </ResponsiveDialogDescription>
                          </ResponsiveDialogHeader>
                          <ResponsiveDialogFooter>
                            <ResponsiveDialogClose asChild>
                              <Button variant="outline">Cancel</Button>
                            </ResponsiveDialogClose>
                            <Button variant="destructive">Delete</Button>
                          </ResponsiveDialogFooter>
                        </ResponsiveDialogContent>
                      </ResponsiveDialog>
                    </div>

                    {/* Usage Notes */}
                    <div className="rounded-lg border p-4 bg-muted/30">
                      <p className="text-sm font-medium mb-2">Usage Guidelines</p>
                      <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                        <li><strong>&lt;7 fields:</strong> Use ResponsiveDialog for CRUD forms</li>
                        <li><strong>≥7 fields:</strong> Use full page edit instead</li>
                        <li><strong>Delete actions:</strong> Always use ResponsiveDialog for confirmation</li>
                        <li><strong>Rich content:</strong> Use full page for articles, long forms</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* CARDS SECTION */}
            <section>
              <SectionHeader
                id="cards"
                title="Cards"
                description="Card components with header, content, and footer"
              />

              <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Card Title</CardTitle>
                    <CardDescription>Card description goes here</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p>Card content area. This is where the main content lives.</p>
                  </CardContent>
                  <CardFooter>
                    <Button>Action</Button>
                  </CardFooter>
                </Card>

                <Card>
                  <CardHeader className="border-b">
                    <CardTitle>With Border</CardTitle>
                    <CardDescription>Header has border-b class</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <p>Content with padding top for border spacing.</p>
                  </CardContent>
                  <CardFooter className="border-t justify-end gap-2">
                    <Button variant="outline">Cancel</Button>
                    <Button>Save</Button>
                  </CardFooter>
                </Card>
              </div>
            </section>

            {/* FORMS SECTION */}
            <section>
              <SectionHeader
                id="forms"
                title="Form Elements"
                description="Input, Label, and form patterns"
              />

              <div className="mt-6 max-w-md space-y-6">
                {/* Basic Input */}
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" placeholder="name@example.com" />
                </div>

                {/* Disabled Input */}
                <div className="space-y-2">
                  <Label htmlFor="disabled">Disabled Input</Label>
                  <Input id="disabled" disabled defaultValue="Cannot edit" />
                </div>

                {/* Invalid Input */}
                <div className="space-y-2">
                  <Label htmlFor="invalid">Invalid State</Label>
                  <Input id="invalid" aria-invalid="true" defaultValue="Invalid value" />
                  <p className="text-sm text-destructive">This field has an error.</p>
                </div>

                {/* File Input */}
                <div className="space-y-2">
                  <Label htmlFor="file">File Upload</Label>
                  <Input id="file" type="file" />
                </div>
              </div>
            </section>

            {/* RICH TEXT EDITOR SECTION */}
            <section>
              <SectionHeader
                id="rich-text"
                title="Rich Text Editor"
                description="TipTap-based WYSIWYG editor with HTML storage"
              />

              <div className="mt-6 space-y-6">
                {/* Interactive Editor Demo */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Editor Demo</CardTitle>
                    <CardDescription>
                      Full-featured editor with toolbar. Content is stored as HTML.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <RichTextEditor
                      value={editorContent}
                      onChange={setEditorContent}
                      placeholder="Start writing..."
                    />
                  </CardContent>
                </Card>

                {/* HTML Output Preview */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">HTML Output</CardTitle>
                    <CardDescription>
                      Raw HTML generated from the editor above
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <pre className="p-4 bg-muted rounded-md text-xs overflow-auto max-h-64 whitespace-pre-wrap break-all">
                      {editorContent || '(empty)'}
                    </pre>
                  </CardContent>
                </Card>

                {/* Read-only Rendered View */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Read-Only Preview</CardTitle>
                    <CardDescription>
                      How the content appears when rendered (non-editable)
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <RichTextEditor
                      value={editorContent}
                      editable={false}
                    />
                  </CardContent>
                </Card>

                {/* Usage Guidelines */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Usage Guidelines</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg border p-4 bg-muted/30">
                      <ul className="text-sm text-muted-foreground space-y-2 list-disc list-inside">
                        <li><strong>Storage:</strong> Content is stored as HTML</li>
                        <li><strong>API Ingestion:</strong> Markdown from API is converted to HTML once via commonmarker gem</li>
                        <li><strong>Features:</strong> Headings (H1-H3), bold, italic, underline, links, lists, blockquotes, tables, images, YouTube embeds</li>
                        <li><strong>Use case:</strong> Articles, rich content editing on full pages</li>
                        <li><strong>Not for:</strong> Simple forms - use regular inputs instead</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* INTERACTIVE STATES SECTION */}
            <section>
              <SectionHeader
                id="states"
                title="Interactive States"
                description="Hover, focus, disabled, and loading states"
              />

              <div className="mt-6 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Button States</CardTitle>
                    <CardDescription>Interact with these elements to see states</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <p className="text-sm font-medium mb-2">Hover States</p>
                      <p className="text-xs text-muted-foreground mb-3">
                        Hover over buttons to see hover effect
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button>Hover Me</Button>
                        <Button variant="outline">Hover Me</Button>
                        <Button variant="ghost">Hover Me</Button>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-2">Focus States</p>
                      <p className="text-xs text-muted-foreground mb-3">
                        Tab to these elements to see focus rings
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button>Focus Me</Button>
                        <Input placeholder="Focus me" className="w-40" />
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-2">Disabled States</p>
                      <div className="flex flex-wrap gap-2">
                        <Button disabled>Disabled</Button>
                        <Button variant="outline" disabled>Disabled</Button>
                        <Input disabled defaultValue="Disabled" className="w-40" />
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium mb-2">Loading States</p>
                      <div className="flex flex-wrap gap-2">
                        <Button disabled>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading...
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* PATTERNS SECTION */}
            <section>
              <SectionHeader
                id="patterns"
                title="Patterns"
                description="Common layout and UI patterns"
              />

              <div className="mt-6 space-y-8">
                {/* Centered Card Pattern */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Centered Card (Auth/Hero)</CardTitle>
                    <CardDescription>Used for login, signup, hero sections</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg p-4 bg-muted/30 overflow-x-auto">
                      <pre className="text-xs">
{`<div className="min-h-screen flex items-center justify-center p-4">
  <Card className="w-full max-w-md">
    <CardHeader>...</CardHeader>
    <CardContent>...</CardContent>
  </Card>
</div>`}
                      </pre>
                    </div>
                  </CardContent>
                </Card>

                {/* Form Group Pattern */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Form Field Group</CardTitle>
                    <CardDescription>Label + Input + Error message</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="max-w-sm space-y-2">
                      <Label htmlFor="demo">Field Label</Label>
                      <Input id="demo" placeholder="Enter value..." />
                      <p className="text-sm text-muted-foreground">
                        Helper text describing the field
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Button Group Pattern */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Button Groups</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Inline actions</p>
                      <div className="flex gap-2">
                        <Button variant="outline">Cancel</Button>
                        <Button>Submit</Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Full-width stack</p>
                      <div className="flex flex-col gap-2 max-w-xs">
                        <Button>Primary Action</Button>
                        <Button variant="outline">Secondary Action</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>

          </main>
        </div>
      </div>
    </>
  )
}
